/**
 * PurchasesOverview — Vue d'ensemble Achats (iter87, 13/02/2026).
 *
 * Miroir de SalesOverview, adapté au métier Achats :
 * - Dépenses période avec filtres + comparaison A vs B
 * - Encours à payer + buckets J+7 / J+30 / en retard
 * - DPO (délai moyen paiement)
 * - Trend 12m + Top 5 fournisseurs avec concentration
 * - Alertes cash-flow / dépendance / dérive
 * - Prochaines factures à payer + derniers paiements sortis
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock, CheckCircle2,
  Building2, Package, Plus, ArrowRight, FileText, Loader2, Target,
} from "lucide-react";
import { cn } from "@/lib/utils";

const eur = (n, p = 0) => `${(Number(n) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: p, maximumFractionDigits: p,
})} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—";
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const ALERT_META = {
  danger:  { cls: "bg-red-50 border-red-200 text-red-800", Icon: AlertTriangle },
  warning: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: Clock },
  info:    { cls: "bg-blue-50 border-blue-200 text-blue-800", Icon: FileText },
  success: { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2 },
};

function BigKpi({ label, value, sub, delta, icon: Icon, accent = "orange", idx = 0, testid }) {
  const accents = {
    orange: "bg-orange-50 text-orange-700",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    purple: "bg-purple-50 text-purple-700",
  };
  const positive = typeof delta === "number" && delta >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04 }}>
      <Card className="p-5 bg-white border-slate-200 rounded-xl card-hover" data-testid={testid}>
        <div className="flex items-start justify-between mb-3">
          <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", accents[accent])}>
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </div>
          {typeof delta === "number" && (
            <div className={cn("flex items-center gap-1 text-xs font-medium",
              // Pour Achats : hausse dépenses = orange, baisse = vert
              positive ? "text-orange-700" : "text-emerald-700")}>
              {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {positive ? "+" : ""}{delta.toFixed(1)} %
            </div>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 font-mono tabular-nums"
              style={{ fontFamily: "Manrope, sans-serif" }}>
          {value}
        </div>
        {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      </Card>
    </motion.div>
  );
}

const MONTHS_FR = [
  { value: "all", label: "Toute l'année" },
  { value: "01", label: "Janvier" }, { value: "02", label: "Février" },
  { value: "03", label: "Mars" }, { value: "04", label: "Avril" },
  { value: "05", label: "Mai" }, { value: "06", label: "Juin" },
  { value: "07", label: "Juillet" }, { value: "08", label: "Août" },
  { value: "09", label: "Septembre" }, { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" }, { value: "12", label: "Décembre" },
];
const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

export default function PurchasesOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("12m");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("all");
  const [compareOn, setCompareOn] = useState(false);
  const [comparePreset, setComparePreset] = useState("last_year");
  const [compareYear, setCompareYear] = useState(new Date().getFullYear() - 1);
  const [compareMonth, setCompareMonth] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (preset && preset !== "custom") params.preset = preset;
      else { params.year = year; if (month !== "all") params.month = month; }
      if (compareOn) {
        if (comparePreset && comparePreset !== "custom") params.compare_preset = comparePreset;
        else { params.compare_year = compareYear; if (compareMonth !== "all") params.compare_month = compareMonth; }
      }
      const r = await api.get("/purchases/overview", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    preset, year, month, compareOn, comparePreset, compareYear, compareMonth,
  ]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "last_year") { setYear(new Date().getFullYear() - 1); setMonth("all"); }
    else if (p === "ytd")   { setYear(new Date().getFullYear());     setMonth("all"); }
    else if (p === "month") { setYear(new Date().getFullYear()); setMonth(new Date().toISOString().slice(5, 7)); }
  };

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500" data-testid="purchases-overview-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du tableau de bord Achats…
      </div>
    );
  }

  const cp = data.current_period;
  const outs = data.outstanding;
  // On coupe la série au mois courant : pas de points 0 dans le futur.
  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const trendData = (data.trend_12m || [])
    .filter((b) => (b.month || "") <= currentMonthKey)
    .map((b) => ({ month: fmtMonth(b.month), Dep: b.ca_ttc }));
  // Delta pertinent uniquement si la période a des données.
  const showDelta = typeof cp.delta_pct_vs_last_year === "number" && cp.count > 0;
  // Bandeau "période vide" — évite l'impression "SaaS cassé".
  const isEmpty = (cp.count || 0) === 0;

  return (
    <div className="space-y-6" data-testid="purchases-overview-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Vue d'ensemble Achats
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pilotage complet — période <span className="font-semibold text-slate-700">{data.period?.label}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/app/purchases/vigilance")}
                   data-testid="qa-vigilance-btn"
                   className="border-amber-200 text-amber-700 hover:bg-amber-50">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Vigilance
          </Button>
          <Button variant="outline" onClick={() => navigate("/app/purchases/supplier-invoices")}
                   data-testid="qa-list-btn">
            <FileText className="w-4 h-4 mr-1.5" /> Liste factures
          </Button>
          <Button onClick={() => navigate("/app/purchases/supplier-invoices/new")}
                   className="bg-orange-600 hover:bg-orange-700 text-white"
                   data-testid="qa-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture achat
          </Button>
        </div>
      </div>

      {/* Filtres période */}
      <Card className="p-3 bg-white border-slate-200 rounded-xl" data-testid="period-filters">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: "month", label: "Mois en cours" },
            { key: "quarter", label: "Trimestre" },
            { key: "ytd", label: "Cumul annuel" },
            { key: "12m", label: "12 mois glissants" },
            { key: "last_year", label: `Année ${new Date().getFullYear() - 1}` },
          ].map((p) => (
            <button key={p.key} onClick={() => applyPreset(p.key)}
                    data-testid={`preset-${p.key}`}
                    className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors",
                      preset === p.key
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              {p.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <select id="purchases-month-select" name="purchases-month-select"
                    value={month} onChange={(e) => { setMonth(e.target.value); setPreset("custom"); }}
                    className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white"
                    data-testid="month-select">
              {MONTHS_FR.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select id="purchases-year-select" name="purchases-year-select"
                    value={year} onChange={(e) => { setYear(Number(e.target.value)); setPreset("custom"); }}
                    className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white"
                    data-testid="year-select">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => setCompareOn((v) => !v)}
                    data-testid="toggle-compare"
                    className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors ml-2",
                      compareOn
                        ? "bg-purple-100 border-purple-300 text-purple-800"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              <TrendingUp className="w-3 h-3 inline mr-1" />
              {compareOn ? "Comparaison active" : "Comparer avec…"}
            </button>
          </div>
        </div>
        {compareOn && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <span className="text-xs text-purple-700 font-medium">🆚 Comparer avec :</span>
            {[
              { key: "last_year", label: `Année ${new Date().getFullYear() - 1}` },
              { key: "month", label: "Mois" },
              { key: "quarter", label: "Trimestre" },
              { key: "ytd", label: "Cumul annuel" },
            ].map((p) => (
              <button key={p.key}
                      onClick={() => { setComparePreset(p.key); setCompareMonth("all"); }}
                      className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors",
                        comparePreset === p.key
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        {compareOn && data.comparison && (
          <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200"
                data-testid="compare-result">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-purple-700 font-medium">Période B</div>
                <div className="font-semibold text-purple-900 mt-0.5">{data.comparison.label}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">Dépenses HT · B</div>
                <div className="font-mono font-semibold text-slate-800 mt-0.5">{eur(data.comparison.ca_ht)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">Δ Dépenses (A vs B)</div>
                {data.comparison.delta_ht_pct == null ? (
                  <div className="text-slate-400 mt-0.5">—</div>
                ) : (
                  <div className={cn("font-mono font-semibold mt-0.5 flex items-center gap-1",
                    data.comparison.delta_ht_pct >= 0 ? "text-orange-700" : "text-emerald-700")}>
                    {data.comparison.delta_ht_pct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {data.comparison.delta_ht_pct >= 0 ? "+" : ""}{data.comparison.delta_ht_pct.toFixed(1)} %
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">Nb factures B</div>
                <div className="font-mono font-semibold text-slate-800 mt-0.5">{data.comparison.count}</div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Zone 1 — À traiter maintenant (alertes cash-flow + récurrences manquantes) */}
      {(data.alerts || []).length > 0 && (
        <div data-testid="zone-actions">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-6 w-1 rounded-full bg-red-500" />
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
              À traiter maintenant
            </h2>
            <span className="text-xs text-slate-400">
              · {data.alerts.length} action{data.alerts.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-2" data-testid="alerts-block">
            {data.alerts.map((a, i) => {
            const { cls, Icon } = ALERT_META[a.level] || ALERT_META.warning;
            const Wrap = a.action_route
              ? ({ children }) => <button onClick={() => navigate(a.action_route)} className="w-full text-left">{children}</button>
              : ({ children }) => <div>{children}</div>;
            return (
              <Wrap key={i}>
                <div className={cn("px-4 py-3 rounded-lg border flex items-center gap-3 text-sm", cls,
                                    a.action_route && "hover:brightness-95 cursor-pointer")}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{a.message}</span>
                  {a.action_label && (
                    <span className="text-xs font-medium underline flex items-center gap-1">
                      {a.action_label} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </Wrap>
            );
          })}
          </div>
        </div>
      )}

      {/* Bandeau info si période vide (aide UX) */}
      {isEmpty && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm text-blue-900 flex items-center gap-2"
             data-testid="empty-period-banner">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            Aucune facture fournisseur sur la période <b>{data.period?.label}</b>.
            Essaie une période plus large.
          </span>
          <button onClick={() => applyPreset("12m")}
                  className="text-xs font-medium underline hover:no-underline">
            Voir 12 mois glissants
          </button>
        </div>
      )}

      {/* Zone 2 — Pilotage du mois */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-1 rounded-full bg-orange-500" />
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
            Pilotage — {data.period?.label}
          </h2>
        </div>
      </div>

      {/* 4 Big KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BigKpi label={`Dépenses · ${data.period?.label}`}
                 value={eur(cp.ca_ht)}
                 sub={`${cp.count} facture${cp.count > 1 ? "s" : ""} · TTC ${eur(cp.ca_ttc)}`}
                 delta={showDelta ? cp.delta_pct_vs_last_year : undefined}
                 icon={Package} accent="orange" idx={0} testid="kpi-period" />
        <BigKpi label="À payer J+7"
                 value={eur(outs.due_7_days.ttc)}
                 sub={`${outs.due_7_days.count} facture${outs.due_7_days.count > 1 ? "s" : ""} — tension cash`}
                 icon={AlertTriangle}
                 accent={outs.due_7_days.count > 0 ? "amber" : "emerald"}
                 idx={1} testid="kpi-due-7" />
        <BigKpi label="Encours total"
                 value={eur(outs.total_ttc)}
                 sub={`${outs.count} facture${outs.count > 1 ? "s" : ""} à payer`}
                 icon={Building2}
                 accent={outs.overdue.count > 0 ? "red" : "blue"}
                 idx={2} testid="kpi-outstanding" />
        <BigKpi label="DPO tenant"
                 value={data.dpo_days == null ? "—" : `${data.dpo_days > 0 ? "+" : ""}${data.dpo_days} j`}
                 sub={data.dpo_days == null ? "aucun paiement récent"
                       : data.dpo_days > 0 ? "payé en retard — négocier"
                       : data.dpo_days > -5 ? "payé à échéance"
                       : "payé en avance — optimiser"}
                 icon={Clock}
                 accent={data.dpo_days == null ? "purple"
                          : data.dpo_days > 5 ? "red"
                          : data.dpo_days > 0 ? "amber" : "emerald"}
                 idx={3} testid="kpi-dpo" />
      </div>

      {/* Zone 3 — Tendance & concentration */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-1 rounded-full bg-slate-400" />
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
            Tendance & concentration
          </h2>
        </div>
      </div>

      {/* Row: Buckets échéances + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4 p-5 bg-white border-slate-200 rounded-xl" data-testid="buckets-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-4"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            Échéances à venir
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 rounded-lg bg-red-50 border border-red-100">
              <div>
                <div className="text-[10px] uppercase text-red-700 font-medium">En retard</div>
                <div className="text-xs text-red-800 mt-0.5">{outs.overdue.count} facture{outs.overdue.count > 1 ? "s" : ""}</div>
              </div>
              <div className="text-base font-mono font-semibold text-red-900">{eur(outs.overdue.ttc)}</div>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-amber-50 border border-amber-100">
              <div>
                <div className="text-[10px] uppercase text-amber-700 font-medium">Sous 7 jours</div>
                <div className="text-xs text-amber-800 mt-0.5">{outs.due_7_days.count} facture{outs.due_7_days.count > 1 ? "s" : ""}</div>
              </div>
              <div className="text-base font-mono font-semibold text-amber-900">{eur(outs.due_7_days.ttc)}</div>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-blue-50 border border-blue-100">
              <div>
                <div className="text-[10px] uppercase text-blue-700 font-medium">Sous 30 jours</div>
                <div className="text-xs text-blue-800 mt-0.5">{outs.due_30_days.count} facture{outs.due_30_days.count > 1 ? "s" : ""}</div>
              </div>
              <div className="text-base font-mono font-semibold text-blue-900">{eur(outs.due_30_days.ttc)}</div>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-8 p-5 bg-white border-slate-200 rounded-xl" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900"
                   style={{ fontFamily: "Manrope, sans-serif" }}>
                Dépenses 12 mois glissants
              </h3>
              <p className="text-xs text-slate-500">Factures fournisseurs enregistrées (TTC)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="purchases-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ea580c" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ea580c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <RTooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                         formatter={(v) => [eur(v), "Dépenses TTC"]} />
              <Area type="monotone" dataKey="Dep" stroke="#ea580c" strokeWidth={2} fill="url(#purchases-fill)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Row: Top suppliers + Upcoming + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-6 p-5 bg-white border-slate-200 rounded-xl" data-testid="top-suppliers-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Top 5 fournisseurs (12m) — concentration
            </h3>
            <Link to="/app/suppliers" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
              Tous <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {(data.top_suppliers || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">Aucun fournisseur actif</div>
          ) : (
            <div className="space-y-2.5">
              {data.top_suppliers.map((s, i) => {
                const max = data.top_suppliers[0].ttc;
                const pct = max > 0 ? (s.ttc / max) * 100 : 0;
                return (
                  <div key={s.supplier_id} data-testid={`top-supplier-${i}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-semibold text-orange-700 shrink-0">
                          {i + 1}
                        </div>
                        <span className="text-sm font-medium text-slate-800 truncate" title={s.name}>{s.name}</span>
                        {s.concentration_pct > 30 && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-[9px] font-semibold text-red-700">
                            {s.concentration_pct}% ⚠️
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-mono font-semibold text-slate-900 shrink-0">{eur(s.ttc)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                            style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {s.count} facture{s.count > 1 ? "s" : ""} · {s.concentration_pct}% du total
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-6 p-5 bg-white border-slate-200 rounded-xl" data-testid="upcoming-due-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            📅 Prochaines factures à payer
          </h3>
          {(data.upcoming_due || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">Rien à payer prochainement</div>
          ) : (
            <div className="space-y-1.5">
              {data.upcoming_due.map((inv) => (
                <div key={inv.id}
                      className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-50"
                      data-testid={`upcoming-${inv.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("w-1.5 h-1.5 rounded-full shrink-0",
                      inv.urgency === "high" ? "bg-red-500" : "bg-amber-500")} />
                    <div className="min-w-0">
                      <div className="text-sm font-mono text-slate-800 truncate">{inv.number || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate">{inv.supplier_name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-500 font-mono">{fmtDate(inv.due_date)}</div>
                    <div className="text-sm font-mono font-semibold text-slate-800">{eur(inv.remaining_ttc)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Derniers paiements sortis */}
      <Card className="p-5 bg-white border-slate-200 rounded-xl" data-testid="recent-paid-card">
        <h3 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Manrope, sans-serif" }}>
          💸 Derniers paiements sortis (30 jours)
        </h3>
        {(data.recent_paid || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic py-6 text-center">Aucun paiement récent</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {data.recent_paid.map((inv) => (
              <div key={inv.id}
                    className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-50"
                    data-testid={`recent-paid-${inv.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-mono text-slate-800 truncate">{inv.number || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{inv.supplier_name}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500 font-mono">{fmtDate(inv.paid_at)}</div>
                  <div className="text-sm font-mono font-semibold text-orange-700">−{eur(inv.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
